import { formatMessage } from '@/utils/formatMessage';

export const simulationIndicatorsContent = {
  'en-US': {
    panel: {
      title: 'Technical indicators',
      selectButton: 'Select indicators ({count})',
      refreshButton: 'Refresh',
      refreshing: 'Loading...',
      removeTooltip: 'Remove {name}',
      loadingIndicator: 'Loading {name} data...',
      noIndicator: 'No {name} data',
      noSelectionPrompt: 'Please select technical indicators to display',
      chooseButton: 'Choose indicators',
      initialLoading: 'Loading technical indicator data...'
    },
    chart: {
      title: 'Technical indicators',
      zoomIn: 'Zoom in',
      zoomOut: 'Zoom out',
      resetZoom: 'Reset zoom',
      syncZoom: 'Synchronise zoom',
      showButton: 'Show indicators',
      hideButton: 'Hide indicators',
      toggleShow: 'Show {name}',
      toggleHide: 'Hide {name}',
      noData: 'No valid candlestick data available for technical indicators',
      showPrompt: 'Click "Show indicators" to view the charts'
    },
    demoPage: {
      title: 'Technical indicator demo',
      description:
        'This page demonstrates the technical indicator component by plotting multiple indicator charts with mock market data.'
    },
    fixed: {
      title: 'Technical indicators (fixed)',
      showDemo: 'Show demo',
      hideDemo: 'Hide demo',
      prompt: 'Click "Show demo" to view the updated technical indicator charts',
      macdTitle: 'MACD demo',
      rsiTitle: 'RSI demo'
    },
    safe: {
      title: 'Technical indicators (safe)'
    },
    testPage: {
      title: 'Technical indicator component test',
      description: 'A lightweight page used to verify that the technical indicator component functions correctly.',
      sectionTitle: 'Basic functionality test'
    },
    indicators: {
      categories: [
        { key: 'trend', title: 'Trend indicators', indicators: ['macd', 'bollinger', 'adx'] },
        { key: 'oscillator', title: 'Oscillator indicators', indicators: ['rsi', 'aroon', 'cmf'] },
        { key: 'volume', title: 'Volume indicators', indicators: ['obv', 'volume'] }
      ],
      definitions: {
        macd: {
          name: 'MACD',
          description: 'Moving Average Convergence Divergence',
          params: ['Fast line', 'Slow line', 'Signal line'],
          legend: {
            macd: 'MACD',
            signal: 'Signal',
            histogram: 'Histogram'
          }
        },
        bollinger: {
          name: 'Bollinger Bands',
          description: 'Upper and lower bands around a moving average',
          params: ['Upper band', 'Middle band', 'Lower band'],
          legend: {
            close: 'Close price',
            upper: 'Upper band',
            lower: 'Lower band'
          }
        },
        adx: {
          name: 'ADX (Average Directional Index)',
          description: 'Measures the strength of a trend',
          params: ['Trend strength'],
          legend: {
            adx: 'ADX'
          }
        },
        rsi: {
          name: 'RSI',
          description: 'Relative Strength Index',
          params: ['Overbought 70', 'Oversold 30'],
          legend: {
            rsi: 'RSI'
          }
        },
        aroon: {
          name: 'Aroon',
          description: 'Tracks time since highs and lows',
          params: ['Aroon up', 'Aroon down'],
          legend: {
            up: 'Aroon up',
            down: 'Aroon down'
          }
        },
        obv: {
          name: 'OBV',
          description: 'On-Balance Volume indicator',
          params: ['Accumulated volume'],
          legend: {
            obv: 'OBV'
          }
        },
        cmf: {
          name: 'CMF',
          description: 'Chaikin Money Flow indicator',
          params: ['Money flow'],
          legend: {
            cmf: 'CMF'
          }
        },
        volume: {
          name: 'Volume',
          description: 'Volume histogram',
          params: ['Volume'],
          legend: {
            volume: 'Volume'
          }
        }
      }
    }
  },
  'zh-CN': {
    panel: {
      title: '技术指标',
      selectButton: '选择指标 ({count})',
      refreshButton: '刷新',
      refreshing: '加载中...',
      removeTooltip: '移除 {name}',
      loadingIndicator: '正在加载 {name} 数据...',
      noIndicator: '暂无 {name} 数据',
      noSelectionPrompt: '请选择要显示的技术指标',
      chooseButton: '选择指标',
      initialLoading: '正在加载技术指标数据...'
    },
    chart: {
      title: '技术指标',
      zoomIn: '放大',
      zoomOut: '缩小',
      resetZoom: '重置缩放',
      syncZoom: '同步缩放',
      showButton: '显示指标',
      hideButton: '隐藏指标',
      toggleShow: '显示{name}',
      toggleHide: '隐藏{name}',
      noData: '暂无有效的K线数据用于生成技术指标',
      showPrompt: '点击“显示指标”查看技术指标图表'
    },
    demoPage: {
      title: '技术指标演示',
      description: '该页面使用模拟数据展示多个技术指标图表，用于演示组件的整体效果。'
    },
    fixed: {
      title: '技术指标（修复版）',
      showDemo: '显示演示',
      hideDemo: '隐藏演示',
      prompt: '点击“显示演示”查看修复后的技术指标图表',
      macdTitle: 'MACD 演示',
      rsiTitle: 'RSI 演示'
    },
    safe: {
      title: '技术指标（安全版）'
    },
    testPage: {
      title: '技术指标组件测试',
      description: '这是一个简单的测试页面，用于验证技术指标组件是否正常工作。',
      sectionTitle: '基础功能测试'
    },
    indicators: {
      categories: [
        { key: 'trend', title: '趋势指标', indicators: ['macd', 'bollinger', 'adx'] },
        { key: 'oscillator', title: '震荡指标', indicators: ['rsi', 'aroon', 'cmf'] },
        { key: 'volume', title: '成交量指标', indicators: ['obv', 'volume'] }
      ],
      definitions: {
        macd: {
          name: 'MACD',
          description: '移动平均收敛散度指标',
          params: ['快线', '慢线', '信号线'],
          legend: {
            macd: 'MACD',
            signal: '信号线',
            histogram: '柱状图'
          }
        },
        bollinger: {
          name: '布林带',
          description: '围绕移动平均线的上下轨',
          params: ['上轨', '中轨', '下轨'],
          legend: {
            close: '收盘价',
            upper: '上轨',
            lower: '下轨'
          }
        },
        adx: {
          name: 'ADX（平均趋向指数）',
          description: '衡量趋势强度的指标',
          params: ['趋势强度'],
          legend: {
            adx: 'ADX'
          }
        },
        rsi: {
          name: 'RSI',
          description: '相对强弱指数',
          params: ['超买 70', '超卖 30'],
          legend: {
            rsi: 'RSI'
          }
        },
        aroon: {
          name: 'Aroon 指标',
          description: '追踪最近高点与低点的时间',
          params: ['上线', '下线'],
          legend: {
            up: 'Aroon 上线',
            down: 'Aroon 下线'
          }
        },
        obv: {
          name: 'OBV 能量潮',
          description: '累计成交量的动量指标',
          params: ['累积量'],
          legend: {
            obv: 'OBV'
          }
        },
        cmf: {
          name: 'CMF 资金流量',
          description: '衡量价格与成交量结合的资金流向',
          params: ['资金流向'],
          legend: {
            cmf: 'CMF'
          }
        },
        volume: {
          name: '成交量',
          description: '成交量柱状图',
          params: ['成交量'],
          legend: {
            volume: '成交量'
          }
        }
      }
    }
  }
};

/**
 * Helper to retrieve a locale-specific message with optional interpolation.
 *
 * @param {keyof typeof simulationIndicatorsContent} locale
 * @param {string} path
 * @param {Record<string, string | number>} [params]
 * @returns {string}
 */
export function getSimulationIndicatorMessage(locale, path, params) {
  const localeContent =
    simulationIndicatorsContent[locale] ?? simulationIndicatorsContent['en-US'];

  const segments = path.split('.');
  let current = localeContent;

  for (const segment of segments) {
    if (current && Object.prototype.hasOwnProperty.call(current, segment)) {
      current = current[segment];
    } else {
      current = null;
      break;
    }
  }

  if (typeof current === 'string') {
    return formatMessage(current, params);
  }

  return '';
}
